import React, { useState } from 'react';
import {
    LayoutGrid, ShoppingBag, Package, Users, TrendingUp, User, Settings,
    Warehouse, Store, LogOut, CheckCircle, ClipboardList, BarChart3,
    Shield, FileText, Building2, CreditCard, Menu, X, Target, Download
} from 'lucide-react';
import { usePWA } from '../../../context/PWAContext';

const MobileNavbar = ({ currentRole, activeView, onViewChange, onLogout, subscriptionPlan }) => {
    const [isMoreOpen, setIsMoreOpen] = useState(false);
    const { promptInstall, openInstallModal, isInstalled } = usePWA();

    const ANALYTICS_PLANS = ['STANDARD', 'PROFESSIONAL', 'ENTERPRISE'];
    const hasAnalytics = !subscriptionPlan || ANALYTICS_PLANS.includes(subscriptionPlan?.toUpperCase());

    // All views per role — keep in sync with Sidebar.jsx
    const allViews = {
        SYSTEM_ADMIN: [
            { id: 'dashboard',          label: 'Dashboard', icon: LayoutGrid },
            { id: 'companies',          label: 'Companies', icon: Building2 },
            { id: 'subscription-plans', label: 'Plans',     icon: CreditCard },
            { id: 'staff',              label: 'Staff',     icon: Users },
            { id: 'analytics',          label: 'Analytics', icon: TrendingUp },
        ],
        TENANT_OWNER: [
            { id: 'dashboard',        label: 'Dashboard',     icon: LayoutGrid },
            { id: 'orders',           label: 'Orders',        icon: ShoppingBag },
            { id: 'pos',              label: 'POS',           icon: Store },
            { id: 'loading-requests', label: 'VAN',           icon: Warehouse },
            { id: 'customers',        label: 'Customers',     icon: Users },
            { id: 'products',         label: 'Inventory',     icon: Package },
            { id: 'sales-report',     label: 'Ops Log',       icon: ClipboardList },
            { id: 'sales-targets',    label: 'Sales Targets', icon: Target },
            { id: 'daily-reports',    label: 'Reports',       icon: BarChart3 },
            ...(hasAnalytics ? [{ id: 'operations-analytics', label: 'Performance', icon: TrendingUp }] : []),
            { id: 'analytics',        label: 'Analytics',     icon: TrendingUp },
            { id: 'staff',            label: 'Staff',         icon: User },
            { id: 'settings',         label: 'Settings',      icon: Settings },
        ],
        TENANT_ADMIN: [
            { id: 'dashboard',        label: 'Dashboard',     icon: LayoutGrid },
            { id: 'orders',           label: 'Orders',        icon: ShoppingBag },
            { id: 'pos',              label: 'POS',           icon: Store },
            { id: 'loading-requests', label: 'VAN',           icon: Warehouse },
            { id: 'customers',        label: 'Customers',     icon: Users },
            { id: 'products',         label: 'Inventory',     icon: Package },
            { id: 'sales-report',     label: 'Ops Log',       icon: ClipboardList },
            { id: 'sales-targets',    label: 'Sales Targets', icon: Target },
            { id: 'daily-reports',    label: 'Reports',       icon: BarChart3 },
            ...(hasAnalytics ? [{ id: 'operations-analytics', label: 'Performance', icon: TrendingUp }] : []),
            { id: 'analytics',        label: 'Analytics',     icon: TrendingUp },
            { id: 'staff',            label: 'Staff',         icon: User },
            { id: 'settings',         label: 'Settings',      icon: Settings },
        ],
        SALES_MANAGER: [
            { id: 'dashboard',        label: 'Dashboard',     icon: LayoutGrid },
            { id: 'orders',           label: 'Orders',        icon: ShoppingBag },
            { id: 'pos',              label: 'POS',           icon: Store },
            { id: 'loading-requests', label: 'VAN',           icon: Warehouse },
            { id: 'customers',        label: 'Customers',     icon: Users },
            { id: 'products',         label: 'Inventory',     icon: Package },
            { id: 'staff',            label: 'Staff',         icon: User },
            { id: 'sales-report',     label: 'Ops Log',       icon: ClipboardList },
            { id: 'sales-targets',    label: 'Sales Targets', icon: Target },
            { id: 'daily-reports',    label: 'Reports',       icon: BarChart3 },
            ...(hasAnalytics ? [{ id: 'operations-analytics', label: 'Performance', icon: TrendingUp }] : []),
            { id: 'analytics',        label: 'Analytics',     icon: TrendingUp },
        ],
        SALES_PERSON: [
            { id: 'dashboard',        label: 'Dashboard',     icon: LayoutGrid },
            { id: 'sales-orders',     label: 'Orders',        icon: ShoppingBag },
            { id: 'pos',              label: 'POS',           icon: Store },
            { id: 'loading-requests', label: 'VAN',           icon: Warehouse },
            { id: 'customers',        label: 'Customers',     icon: Users },
            { id: 'products',         label: 'Inventory',     icon: Package },
            { id: 'sales-report',     label: 'Ops Log',       icon: ClipboardList },
            { id: 'daily-reports',    label: 'Reports',       icon: BarChart3 },
            ...(hasAnalytics ? [{ id: 'operations-analytics', label: 'Performance', icon: TrendingUp }] : []),
            { id: 'analytics',        label: 'Analytics',     icon: TrendingUp }
        ],
        WAREHOUSE_PERSON: [
            { id: 'dashboard',        label: 'Dashboard',     icon: LayoutGrid },
            { id: 'loading-requests', label: 'VAN',           icon: Warehouse },
            { id: 'warehouse-orders', label: 'New Orders',    icon: ShoppingBag },
            { id: 'warehouse-confirmed', label: 'Confirmed',  icon: CheckCircle },
        ],
    };

    const roleViews = (allViews[currentRole] || []).filter(v => v.id !== 'analytics' || hasAnalytics);

    // Extra items always appended at the end
    const extras = [
        { id: 'profile', label: 'Profile', icon: User },
        { id: '__install__', label: isInstalled ? 'Installed' : 'Install App', icon: Download, isInstall: true },
        { id: '__logout__', label: 'Logout', icon: LogOut, isLogout: true },
    ];

    const allItems = [...roleViews, ...extras];

    // Priority view IDs to prioritize on mobile bottom navbar
    const priorityViewIds = [
        'dashboard',
        'orders',
        'sales-orders',
        'pos',
        'loading-requests',
        'warehouse-orders',
        'warehouse-confirmed'
    ];

    // More button should only show up if there are more than 5 menu items in total
    const shouldGroup = currentRole !== 'SYSTEM_ADMIN' && allItems.length > 5;
    
    const visibleItems = [];
    const moreItems = [];

    if (shouldGroup) {
        allItems.forEach(item => {
            if (item.isLogout || item.id === '__install__' || item.id === 'profile') {
                moreItems.push(item);
            } else if (priorityViewIds.includes(item.id) && visibleItems.length < 4) {
                visibleItems.push(item);
            } else {
                moreItems.push(item);
            }
        });
    } else {
        allItems.forEach(item => {
            if (item.isLogout) {
                return;
            }
            visibleItems.push(item);
        });
    }

    const isWarehouseView = (id) => ['loading-requests', 'offload-requests', 'transfer-history', 'warehouse-dashboard'].includes(id);

    const isMoreActive = shouldGroup && moreItems.some(item => 
        item.id === activeView || (item.id === 'loading-requests' && isWarehouseView(activeView))
    );

    // Construct final navbar items array (max 5 items on bottom bar)
    const navBarItems = [
        ...visibleItems,
        ...(shouldGroup ? [{ id: 'more', label: 'More', icon: Menu, isMoreTrigger: true }] : []),
        ...(!shouldGroup ? extras.filter(e => e.isLogout) : [])
    ];

    const handleTap = (item) => {
        setIsMoreOpen(false);
        if (item.isLogout) {
            onLogout();
        } else if (item.isInstall) {
            promptInstall();
        } else if (item.isMoreTrigger) {
            setIsMoreOpen(prev => !prev);
        } else {
            onViewChange(item.id);
        }
    };

    return (
        <div className="md:hidden">
            {/* Bottom Navigation Bar */}
            <div className="fixed bottom-0 left-0 right-0 z-50 pb-safe bg-white/95 backdrop-blur-xl border-t border-gray-100 shadow-[0_-4px_24px_rgba(0,0,0,0.07)]">
                <nav className="flex items-stretch h-16 px-1">
                    {navBarItems.map((item) => {
                        const Icon = item.icon;
                        const isActive = !item.isLogout && !item.isMoreTrigger && (
                            activeView === item.id || (item.id === 'loading-requests' && isWarehouseView(activeView))
                        );
                        const isMoreButtonActive = item.isMoreTrigger && (isMoreOpen || isMoreActive);

                        return (
                            <button
                                key={item.id}
                                onClick={() => handleTap(item)}
                                title={item.label}
                                className={`
                                    relative flex-1 min-w-[44px] flex flex-col items-center justify-center gap-0.5
                                    transition-all duration-200 select-none
                                    ${isActive || isMoreButtonActive
                                        ? 'text-blue-600'
                                        : item.isLogout
                                            ? 'text-red-400 hover:text-red-600'
                                            : 'text-gray-400 hover:text-gray-600'
                                    }
                                `}
                            >
                                {(isActive || isMoreButtonActive) && (
                                    <span className="absolute top-0 left-1/2 -translate-x-1/2 w-6 h-[3px] bg-blue-500 rounded-b-full" />
                                )}

                                <Icon
                                    size={18}
                                    strokeWidth={isActive || isMoreButtonActive ? 2.5 : 1.8}
                                    className={`transition-transform duration-200 ${isActive || isMoreButtonActive ? 'scale-110' : ''}`}
                                />

                                <span
                                    className={`text-[8px] leading-tight font-bold tracking-wide whitespace-nowrap transition-all duration-200 ${
                                        isActive || isMoreButtonActive
                                            ? 'text-blue-600 opacity-100'
                                            : item.isLogout
                                                ? 'text-red-400'
                                                : 'text-gray-400 opacity-80'
                                    }`}
                                >
                                    {item.label}
                                </span>
                            </button>
                        );
                    })}
                </nav>
            </div>

            {/* Bottom Sheet Menu for 'More' Items */}
            {shouldGroup && (
                <>
                    {/* Backdrop */}
                    <div
                        onClick={() => setIsMoreOpen(false)}
                        className={`fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40 transition-opacity duration-300 ${
                            isMoreOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
                        }`}
                    />

                    {/* Bottom Sheet container */}
                    <div
                        className={`fixed bottom-16 left-0 right-0 z-40 bg-white rounded-t-2xl shadow-[0_-8px_30px_rgb(0,0,0,0.12)] border-t border-gray-100 p-4 pb-6 transition-all duration-300 ease-out transform ${
                            isMoreOpen ? 'translate-y-0 opacity-100 pointer-events-auto' : 'translate-y-full opacity-0 pointer-events-none'
                        }`}
                    >
                        {/* Drag Handle Style bar */}
                        <div className="w-12 h-1 bg-gray-200 rounded-full mx-auto mb-4" />

                        <div className="flex justify-between items-center mb-4 px-2">
                            <span className="text-sm font-bold text-gray-500 uppercase tracking-wider">Navigation</span>
                            <button
                                onClick={() => setIsMoreOpen(false)}
                                className="p-1 hover:bg-gray-100 rounded-full transition-colors text-gray-400"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        <div className="grid grid-cols-3 gap-3 max-h-[50vh] overflow-y-auto px-1 py-1 no-scrollbar">
                            {moreItems.map((item) => {
                                const Icon = item.icon;
                                const isActive = !item.isLogout && (
                                    activeView === item.id || (item.id === 'loading-requests' && isWarehouseView(activeView))
                                );

                                return (
                                    <button
                                        key={item.id}
                                        onClick={() => handleTap(item)}
                                        className={`
                                            flex flex-col items-center justify-center p-3 rounded-xl border transition-all duration-200
                                            ${isActive
                                                ? 'bg-blue-50 border-blue-100 text-blue-600 shadow-sm'
                                                : item.isLogout
                                                    ? 'bg-red-50/50 border-red-100 text-red-500 hover:bg-red-50 hover:text-red-600'
                                                    : 'bg-gray-50/50 border-gray-100 text-gray-600 hover:bg-gray-50 hover:text-gray-800'
                                            }
                                        `}
                                    >
                                        <Icon size={20} className="mb-1.5" />
                                        <span className="text-[10px] font-bold text-center leading-tight whitespace-nowrap truncate w-full">
                                            {item.label}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default MobileNavbar;

